import React from 'react';
import { useDropzone } from 'react-dropzone';
import { getFilesFromEvent } from '../../utils/htmlDeploy/gatherFiles';
import AeroIcon from '../../components/Aero/AeroIcon';

/**
 * The drop zone for a new version of an HTML site: a folder, zip or a few
 * files, with deploySite's progress while it runs. Used by a site's Overview
 * ("Drop a new version") and a test copy's Setup ("Upload to this test"), so
 * both read and behave the same. The deploy itself stays with the caller.
 *
 *   onDrop     (acceptedFiles) => void, the caller gathers and deploys
 *   deploying  true while a deploy runs (disables the zone, shows progress)
 *   progress   deploySite's onProgress value, or null
 *   title      the first line ("Drop a new version")
 *   hint       the second line
 *   dragText   shown while files are over the zone
 */
export function deployProgressText(progress) {
  if (!progress) return null;
  if (progress.phase === 'hashing') return `Hashing ${progress.hashed}/${progress.total}…`;
  if (progress.phase === 'uploading') {
    const pct = progress.totalUploadBytes
      ? Math.round((progress.uploadedBytes / progress.totalUploadBytes) * 100)
      : 100;
    return `Uploading ${progress.blobsUploaded}/${progress.blobsTotal} files (${pct}%)…`;
  }
  if (progress.phase === 'publishing') return 'Publishing…';
  return null;
}

export default function SiteDropZone({
  onDrop, deploying = false, disabled = false, progress = null,
  title = 'Drop a new version', hint = '', dragText = 'Drop the folder to publish',
  buttonLabel = 'Choose files',
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    getFilesFromEvent,
    disabled: deploying || disabled,
    multiple: true,
  });
  const progressText = deployProgressText(progress);
  return (
    <div
      {...getRootProps({
        className: `aero-sites-dropzone${isDragActive ? ' active' : ''}${deploying ? ' disabled' : ''}`,
      })}
    >
      <input {...getInputProps({ webkitdirectory: 'true', directory: 'true' })} />
      {deploying ? (
        <p className={progressText ? 'aero-sites-progress' : undefined}>{progressText || 'Publishing…'}</p>
      ) : isDragActive ? (
        <p>{dragText}</p>
      ) : (
        <>
          <AeroIcon name="up" className="aero-sites-dropzone-icon" />
          <span className="aero-sites-dropzone-text">
            <p>{title}</p>
            {hint && <p className="aero-sites-dropzone-hint">{hint}</p>}
          </span>
          <span className="aero-sites-dropzone-btn">{buttonLabel}</span>
        </>
      )}
    </div>
  );
}
